import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import type { SxProps, Theme } from '@mui/material/styles';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Typography from '@mui/material/Typography';
import {
  AdministrativeArea,
  getV1CompaniesCompanyIdDriverDaysDriverDayIdTravelledDistances,
  getV1CompaniesCompanyIdJobsJobIdTravelledDistances,
  getV1CompaniesCompanyIdLoadsLoadIdTravelledDistances,
  getV1CompaniesCompanyIdOrdersOrderIdTravelledDistances,
  TravelledDistance_Read,
  TravelledDistanceType,
} from '@treadinc/horizon-api-spec';
import { convertLength } from '@turf/helpers';
import { t } from 'i18next';
import { isNull } from 'lodash-es';
import { useCallback, useEffect, useState } from 'react';
import { useOutletContext, useParams } from 'react-router-dom';

import { CommonLoader } from '~components/Loaders/CommonLoader';
import { DEFAULT_API_PAGE_SIZE } from '~constants/consts';
import { DriverDay } from '~hooks/useDriverDays/models';
import { Job } from '~hooks/useJob/models';
import type { LocationHistoryMinimal } from '~hooks/useLocationHistory/models';
import { Order } from '~hooks/useOrders/models';
import {
  type JobSiteUsage,
  type LoadSiteUsage,
  type OrderSiteUsage,
} from '~hooks/useSiteUsages/models';
import { extractPagination } from '~services/pagination';
import { useStores } from '~store';
import { getEffectiveUserCompanyId } from '~utils/user/user-utils';

import DistanceMap, { type GeoModel } from './DistanceMap';

const headerCellSx: SxProps<Theme> = {
  fontWeight: 600,
  backgroundColor: (theme) => theme.semantic.surfaceSunken,
  borderBottom: (theme) => `2px solid ${theme.palette.divider}`,
};

function formatTravelledDistanceType(type: TravelledDistanceType): string {
  const key = type.toLowerCase().replace('_', ' ');
  return t(`admin.geo.travelled_distance_type_${type}`, { defaultValue: key });
}

function formatAdministrativeArea(area: AdministrativeArea): string {
  return area === AdministrativeArea.UNKNOWN ? '—' : area.replace(/_/g, ' ');
}

export const Distances = () => {
  const { jobId, orderId, loadId, driverDayId } = useParams<{
    jobId?: string;
    orderId?: string;
    loadId?: string;
    driverDayId?: string;
  }>();
  const {
    job: outletJob,
    order: outletOrder,
    driverDay: outletDriverDay,
    loadData: outletLoadData,
    siteUsages: outletSiteUsages,
    pings: outletPings,
    isMapDataLoading: outletIsMapDataLoading,
  } = useOutletContext<{
    job: Job | null;
    order: Order | null;
    driverDay: DriverDay | null;
    loadData: {
      travelledDistanceMeters: string | null;
    } | null;
    siteUsages: (JobSiteUsage | OrderSiteUsage | LoadSiteUsage)[];
    pings: LocationHistoryMinimal[];
    isMapDataLoading: boolean;
  }>();
  const { userStore } = useStores();
  const companyId = getEffectiveUserCompanyId(userStore);
  const isFeet = userStore?.userCompany?.isFeet ?? false;

  const [data, setData] = useState<TravelledDistance_Read[]>([]);
  const [totalMeters, setTotalMeters] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  let model: GeoModel | null = null;
  let modelId: string | undefined;
  if (jobId) {
    model = 'jobs';
    modelId = jobId;
  } else if (orderId) {
    model = 'orders';
    modelId = orderId;
  } else if (loadId) {
    model = 'loads';
    modelId = loadId;
  } else if (driverDayId) {
    model = 'driver_days';
    modelId = driverDayId;
  }

  // Update totalMeters reactively when outlet context values arrive
  useEffect(() => {
    let total: string | null = null;
    switch (model) {
      case 'jobs':
        total = outletJob?.jobSummary?.travelledDistanceMeters ?? null;
        break;
      case 'orders':
        total = outletOrder?.orderSummary?.travelledDistanceMeters ?? null;
        break;
      case 'loads':
        total = outletLoadData?.travelledDistanceMeters ?? null;
        break;
      case 'driver_days':
        total = outletDriverDay?.travelledDistanceMeters ?? null;
        break;
    }
    setTotalMeters(total);
  }, [model, outletJob, outletOrder, outletLoadData, outletDriverDay]);

  const fetchAllData = useCallback(async () => {
    if (!companyId || !modelId || !model) return;
    setIsLoading(true);
    setError(null);
    setData([]);
    try {
      let pageAfter: string | undefined;
      let allData: TravelledDistance_Read[] = [];

      const fetchPage = async (after?: string) => {
        const query: Record<string, string | number | undefined> = {
          'page[limit]': DEFAULT_API_PAGE_SIZE,
        };
        if (after) query['page[after]'] = after;

        let response;
        switch (model) {
          case 'jobs':
            response = await getV1CompaniesCompanyIdJobsJobIdTravelledDistances({
              path: { 'company-id': companyId, 'job-id': modelId },
              query,
            });
            break;
          case 'orders':
            response = await getV1CompaniesCompanyIdOrdersOrderIdTravelledDistances({
              path: { 'company-id': companyId, 'order-id': modelId },
              query,
            });
            break;
          case 'loads':
            response = await getV1CompaniesCompanyIdLoadsLoadIdTravelledDistances({
              path: { 'company-id': companyId, 'load-id': modelId },
              query,
            });
            break;
          case 'driver_days':
            response =
              await getV1CompaniesCompanyIdDriverDaysDriverDayIdTravelledDistances({
                path: { 'company-id': companyId, 'driver-day-id': modelId },
                query,
              });
            break;
        }
        return response;
      };

      let isFirstPage = true;
      do {
        const response = await fetchPage(pageAfter);
        const pageData = response.data.data;
        allData = [...allData, ...pageData];
        setData(allData);
        if (isFirstPage) {
          isFirstPage = false;
          setIsLoading(false);
        }
        pageAfter = extractPagination(response).after;
      } while (pageAfter);
    } catch {
      setError(t('error_messages.distances_failed_to_fetch'));
    } finally {
      setIsLoading(false);
    }
  }, [companyId, modelId, model]);

  useEffect(() => {
    fetchAllData();
  }, [fetchAllData]);

  // This guard must stay below every hook — an early return between hooks
  // crashes with React #300 when the params change (HORIZON-FE-2RB family).
  if (!model || !modelId) {
    return (
      <Typography
        sx={{
          color: 'text.secondary',
        }}
      >
        {t('admin.geo.distances_missing_id')}
      </Typography>
    );
  }

  const distanceUnit = isFeet
    ? t('units_of_distance.feet')
    : t('units_of_distance.meters');
  const distanceHeader = t('admin.geo.column_distance', { unit: distanceUnit });

  const formatDistance = (meters: string | null): string => {
    const num = !isNull(meters) ? parseFloat(String(meters)) : NaN;
    if (Number.isNaN(num)) return '—';
    if (isFeet) {
      const feet = convertLength(num, 'meters', 'feet');
      return feet.toFixed(2);
    }
    return num.toFixed(2);
  };

  if (error) {
    return <Typography color="error">{error}</Typography>;
  }

  if (isLoading && data.length === 0) {
    return <CommonLoader />;
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      {companyId && (
        <DistanceMap
          model={model}
          modelId={modelId}
          companyId={companyId}
          siteUsages={outletSiteUsages}
          pings={outletPings}
          isLoading={outletIsMapDataLoading}
        />
      )}

      <TableContainer
        component={Paper}
        sx={{
          border: (theme) => `1px solid ${theme.palette.divider}`,
          borderRadius: 1,
          fontFamily: 'monospace',
          '& .MuiTableCell-root': {
            fontFamily: 'inherit',
            paddingTop: 1,
            paddingBottom: 1,
            lineHeight: 1.2,
            fontSize: '0.8125rem',
          },
          '& .MuiTableRow-root': {
            '& td, & th': {
              borderBottomWidth: 1,
            },
          },
        }}
      >
        <Table size="small" stickyHeader>
          <colgroup>
            <col />
            <col />
            <col />
            <col style={{ width: '1%', minWidth: 160 }} />
          </colgroup>
          <TableHead>
            <TableRow>
              <TableCell sx={headerCellSx}>{t('admin.geo.column_equipment')}</TableCell>
              <TableCell sx={headerCellSx}>
                {t('admin.geo.column_administrative_area')}
              </TableCell>
              <TableCell sx={headerCellSx}>{t('admin.geo.column_type')}</TableCell>
              <TableCell sx={[headerCellSx, { whiteSpace: 'nowrap' }]} align="left">
                {distanceHeader}
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {data.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={4}
                  align="center"
                  sx={{ py: 0.5, color: 'text.secondary' }}
                >
                  {t('admin.geo.distances_empty')}
                </TableCell>
              </TableRow>
            ) : (
              <>
                {data.map((row, index) => (
                  <TableRow
                    key={row.id}
                    sx={{
                      backgroundColor: (theme) =>
                        index % 2 === 1 ? theme.semantic.surfaceSunken : 'transparent',
                    }}
                  >
                    <TableCell>{row.equipment?.name ?? '—'}</TableCell>
                    <TableCell>
                      {formatAdministrativeArea(row.administrative_area)}
                    </TableCell>
                    <TableCell>
                      {formatTravelledDistanceType(row.travelled_distance_type)}
                    </TableCell>
                    <TableCell align="right">
                      {formatDistance(row.travelled_distance_meters)}
                    </TableCell>
                  </TableRow>
                ))}
                {totalMeters !== null && (
                  <TableRow
                    sx={{
                      fontWeight: 600,
                      backgroundColor: (theme) => theme.semantic.surfaceSunken,
                      borderTop: (theme) => `2px solid ${theme.palette.divider}`,
                    }}
                  >
                    <TableCell />
                    <TableCell />
                    <TableCell />
                    <TableCell sx={{ fontWeight: 600 }}>
                      <Box
                        sx={{
                          display: 'flex',
                          justifyContent: 'space-between',
                        }}
                      >
                        <span>{t('common.total')}</span>
                        <span>{formatDistance(totalMeters)}</span>
                      </Box>
                    </TableCell>
                  </TableRow>
                )}
              </>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};
